import { fetchApi } from '@/services/api';

export type ProductMasterListItem = {
  id: number;
  code: string;
  client_code?: string | null;
  name: string;
  is_active: boolean;
  family_name?: string | null;
  supplier_name?: string | null;
  chips?: { packaging_incomplete?: boolean };
};

export type PackagingStatusFilter = 'all' | 'incomplete' | 'complete';

export type ProductMasterListResponse = {
  results: ProductMasterListItem[];
  count: number;
  page: number;
  page_size: number;
};

export type ProductSheet = {
  id: number;
  code: string;
  client_code?: string | null;
  name: string;
  is_active: boolean;
  is_active_is_manual?: boolean;
  product_type?: string | null;
  family_id?: number | null;
  family_name?: string | null;
  family_client_code?: string | null;
  sell_unit_id?: number | null;
  sell_unit_name?: string | null;
  sell_unit_abbreviation?: string | null;
  buy_unit_id?: number | null;
  buy_unit_name?: string | null;
  buy_unit_abbreviation?: string | null;
  peso_unitario?: string | null;
  supplier_id?: number | null;
  supplier_name?: string | null;
  primary_supplier_rule?: string | null;
  primary_is_manual?: boolean;
  primary_rule_previews?: Record<
    string,
    { supplier_id: number; supplier_name?: string | null } | null
  >;
  packaging?: {
    length?: string | null;
    width?: string | null;
    height?: string | null;
    dimension_unit?: string | null;
    weight?: string | null;
    units_per_box?: number | null;
    temperature_zone?: string | null;
    fragile?: boolean;
    stackable?: boolean;
    max_stack_pallets?: number | null;
    is_manual?: boolean;
    effective_unit_volume_m3?: string | null;
  } | null;
  suppliers?: Array<Record<string, unknown>>;
  equivalence?: Record<string, unknown>;
};

export const productMasterService = {
  async list(options: {
    search?: string;
    packagingStatus?: PackagingStatusFilter;
    page?: number;
    pageSize?: number;
  } = {}): Promise<ProductMasterListResponse> {
    const params: Record<string, string | number> = {};
    if (options.search) params.search = options.search;
    if (options.packagingStatus && options.packagingStatus !== 'all') {
      params.packaging_status = options.packagingStatus;
    }
    if (options.page != null) params.page = options.page;
    if (options.pageSize != null) params.page_size = options.pageSize;
    const res = await fetchApi<ProductMasterListResponse>('products/product-master/', {
      params: Object.keys(params).length ? params : undefined,
    });
    return {
      results: res.results || [],
      count: res.count ?? (res.results || []).length,
      page: res.page ?? 1,
      page_size: res.page_size ?? options.pageSize ?? 50,
    };
  },
  async get(productId: number): Promise<ProductSheet> {
    return fetchApi<ProductSheet>(`products/product-master/${productId}/`);
  },
  async patch(productId: number, body: Record<string, unknown>): Promise<ProductSheet> {
    return fetchApi<ProductSheet>(`products/product-master/${productId}/`, {
      method: 'PATCH',
      body,
    });
  },

  async uploadPackagingExcel(file: File): Promise<{
    status: string;
    created: number;
    updated: number;
    skipped_manual: number;
    errors: Array<{ row: number; error: string }>;
    warnings: Array<{ row: number; warning: string }>;
    rows: number;
  }> {
    const { API_BASE_URL } = await import('@/services/api');
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/products/product-master/packaging/upload/`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(err.detail || res.statusText || 'Error al subir packaging');
    }
    return res.json();
  },

  async downloadPackagingTemplate(): Promise<void> {
    // Genera la plantilla en el cliente (evita bloqueos del preview / auth en blob).
    const XLSX = await import('xlsx');
    const headers = [
      'product_code',
      'length',
      'width',
      'height',
      'dimension_unit',
      'weight',
      'units_per_box',
      'temperature_zone',
      'fragile',
      'stackable',
      'max_stack_pallets',
    ];
    const example = ['2036', 20, 15, 19, 'cm', 0.456, 24, 'AMBIENT', false, true, ''];
    const sheet = XLSX.utils.aoa_to_sheet([headers, example]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Packaging');
    XLSX.writeFile(workbook, 'plantilla_packaging_productos.xlsx');
  },
};
